import { useEffect, useState } from 'react';
import { EnterpriseEndpoints } from '../../../utils/endpoints';
import { Permissions } from '../../../utils/permissions';
import { Constants } from '../../../utils/constants';
import { formatCount, formatNaira } from '../../../utils/money';
import { formatLagos } from '../../../utils/datetime';
import { useAuth } from '../../../AuthContext';
import PageHeader from '../../shell/PageHeader';
import {
  Alert,
  Button,
  CopyButton,
  DataTable,
  DateRangeFilter,
  FilterSelect,
  Pagination,
  SearchInput,
  StatusPill,
  TableCard,
} from '../../ui';
import { useServerTable, wholeDayDates } from '../../../hooks/useServerTable';
import EnterpriseDeactivated, { isEnterpriseDeactivated } from './enterprise/EnterpriseDeactivated';
import PayoutDialog from './enterprise/PayoutDialog';
import StoreCell, { TwoLines } from './dashboard/StoreCell';
import StoreSelect from './enterprise/StoreSelect';
import useStores from './enterprise/useStores';

const PROCESSING_POLL_MS = 10000;
const QUEUED_PAYOUT_WINDOW_MS = 30000;

const breadcrumbs = [{ label: 'Home', to: '/layout' }, { label: 'Payouts' }];

const Reference = ({ reference }) => {
  if (!reference) return '—';
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <span>{reference}</span>
      <CopyButton text={reference} label="Copy" />
    </div>
  );
};

const InitiatedBy = ({ initiator }) =>
  initiator?.type === 'enterprise' ? (
    <TwoLines primary={initiator.name} secondary={initiator.email} />
  ) : initiator?.type === 'cashcard' ? (
    'CashCard'
  ) : (
    '—'
  );

const usePolling = (active, refresh) => {
  useEffect(() => {
    if (!active) return undefined;
    const timer = setInterval(refresh, PROCESSING_POLL_MS);
    return () => clearInterval(timer);
  }, [active, refresh]);
};

// "Pay out all" only queues jobs, so its payouts reach the list a few moments after the request returns.
const useQueuedPayoutWindow = () => {
  const [awaitingQueued, setAwaitingQueued] = useState(0);
  useEffect(() => {
    if (!awaitingQueued) return undefined;
    const timer = setTimeout(() => setAwaitingQueued(0), QUEUED_PAYOUT_WINDOW_MS);
    return () => clearTimeout(timer);
  }, [awaitingQueued]);
  return [Boolean(awaitingQueued), () => setAwaitingQueued((count) => count + 1)];
};

const EnterprisePayouts = () => {
  const { hasPermission } = useAuth();
  const canPayOut =
    hasPermission(Permissions.enterprisePayoutsCreate) && hasPermission(Permissions.enterprisePayouts);
  const stores = useStores();
  const [merchantId, setMerchantId] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notice, setNotice] = useState('');

  const table = useServerTable(EnterpriseEndpoints.payouts, {
    sort: '-created_at',
    filters: { status: '' },
    dateParams: wholeDayDates,
    extraParams: { merchant_id: merchantId },
    fallbackError: 'We could not load the payouts. Please try again.',
  });

  const [awaitingQueued, expectQueuedPayouts] = useQueuedPayoutWindow();
  const hasProcessing = table.rows.some((row) => row.status === 'processing');
  usePolling(hasProcessing || awaitingQueued, table.refetchSilently);

  if (isEnterpriseDeactivated(table.requestError)) {
    return (
      <>
        <PageHeader title="Payouts" breadcrumbs={breadcrumbs} />
        <EnterpriseDeactivated />
      </>
    );
  }

  const handlePaidOut = (message) => {
    setNotice(message);
    table.refetch();
    expectQueuedPayouts();
  };

  const columns = [
    {
      key: 'created_at',
      header: 'Date (WAT)',
      sortKey: 'created_at',
      sortDefault: 'desc',
      render: (row) => <span className="whitespace-nowrap">{formatLagos(row.created_at)}</span>,
      filter: (
        <DateRangeFilter
          label="Date"
          from={table.from}
          to={table.to}
          onFromChange={table.setFrom}
          onToChange={table.setTo}
        />
      ),
    },
    { key: 'reference', header: 'Reference', render: (row) => <Reference reference={row.reference} /> },
    {
      key: 'store',
      header: 'Store',
      render: (row) => <StoreCell store={row.store} />,
    },
    {
      key: 'destination',
      header: 'Destination',
      render: (row) => (
        <TwoLines
          primary={row.destination?.account_name}
          secondary={[row.destination?.bank_name, row.destination?.account_number].filter(Boolean).join(' · ')}
        />
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      sortKey: 'amount',
      sortDefault: 'desc',
      render: (row) => formatNaira(row.amount),
    },
    { key: 'fee', header: 'Fee', align: 'right', render: (row) => formatNaira(row.fee) },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusPill status={row.status} />,
      filter: (
        <FilterSelect
          value={table.filters.status ?? ''}
          onChange={(value) => table.setFilter('status', value)}
          options={Constants.withdrawalStatuses}
          label="All"
          ariaLabel="Filter by status"
        />
      ),
    },
    { key: 'initiated_by', header: 'Initiated by', render: (row) => <InitiatedBy initiator={row.initiated_by} /> },
    {
      key: 'updated_at',
      header: 'Updated (WAT)',
      render: (row) => <span className="whitespace-nowrap">{formatLagos(row.updated_at)}</span>,
    },
  ];

  return (
    <>
      <PageHeader
        title="Payouts"
        breadcrumbs={breadcrumbs}
        actions={canPayOut && <Button onClick={() => setDialogOpen(true)}>Pay out</Button>}
      />
      {notice && (
        <Alert variant="success" className="mb-6" onDismiss={() => setNotice('')}>
          {notice}
        </Alert>
      )}
      <TableCard
        title="Payouts"
        summary={{ label: 'Payouts', value: formatCount(table.total), loading: table.loading }}
        toolbar={
          <>
            {stores.length > 0 && <StoreSelect stores={stores} value={merchantId} onChange={setMerchantId} />}
            <SearchInput
              value={table.search}
              onChange={table.setSearch}
              placeholder="Search account number or account name"
            />
          </>
        }
        footer={
          table.total > 0 && (
            <Pagination
              page={table.page}
              pageCount={table.pageCount}
              pageSize={table.pageSize}
              onPageChange={table.setPage}
              onPageSizeChange={table.setPageSize}
            />
          )
        }
      >
        <DataTable
          columns={columns}
          rows={table.rows}
          rowKey={(row) => row.id}
          serial
          serialStart={table.page * table.pageSize}
          loading={table.loading}
          error={table.error}
          invalidDateRange={table.invalidDateRange}
          sort={table.sort}
          onSortChange={table.setSort}
          empty={{ title: 'No payouts', message: 'No payouts match the selected filters.' }}
        />
      </TableCard>
      {canPayOut && (
        <PayoutDialog isOpen={dialogOpen} onClose={() => setDialogOpen(false)} onPaidOut={handlePaidOut} />
      )}
    </>
  );
};

export default EnterprisePayouts;
